/**
 * Error boundary for C1Component.
 * Catches rendering errors from Thesys SDK.
 *
 * @see frontend/workspace/CU-868gzzjua/frontend_research.md - Q6 Error States
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Typography, Button } from '@mui/material';

interface Props {
  children: ReactNode;
  onError?: (error: Error) => void;
  onRetry?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class C1ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[PlanGuide] C1Component error:', {
      error,
      errorMessage: error.message,
      componentStack: errorInfo.componentStack,
    });
    this.props.onError?.(error);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
    this.props.onRetry?.();
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            p: 3,
            textAlign: 'center',
            bgcolor: 'error.light',
            borderRadius: 1,
          }}
        >
          <Typography variant="h6" color="error.dark" gutterBottom>
            Error rendering page content
          </Typography>
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {this.state.error.message}
            </Typography>
          )}
          {this.props.onRetry && (
            <Button variant="outlined" onClick={this.handleRetry}>
              Retry
            </Button>
          )}
        </Box>
      );
    }

    return this.props.children;
  }
}
